<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { sitesStatic } from '../composables/SiteList'
import { getAlerts, type Alert, type DeformationDetails, type ChangeDetectionDetails } from '../composables/alertsList'
import { RouterLink } from 'vue-router'
import type { DataTableRowClickEvent } from 'primevue/datatable'

const alerts = ref<Alert[]>(getAlerts())
const sites = ref(sitesStatic)
const loading = ref(true)
const selectedAlert = ref<Alert | null>(null)
const showAlertDialog = ref(false)
const showActionMessage = ref('')
const showActionMessageTimeout = ref<number | null>(null)

// Computed property to sort sites with alerts to the top
const sortedSites = computed(() => {
  return [...sites.value].sort((a, b) => {
    const aHasAlert = hasAlert(a.name)
    const bHasAlert = hasAlert(b.name)
    if (aHasAlert && !bHasAlert) return -1
    if (!aHasAlert && bHasAlert) return 1
    return a.name.localeCompare(b.name)
  })
})

// Simulate API call
onMounted(() => {
  setTimeout(() => {
    loading.value = false
  }, 1000)
})

const generateRandomDeformationDetails = (): DeformationDetails => {
  const features = ['coastal', 'infrastructure (ancillary)', 'infrastructure (core)', 'water'] as const
  const thresholds = [0.1, 0.5, 1] // 1mm, 5mm, 1cm in cm
  return {
    threshold: thresholds[Math.floor(Math.random() * thresholds.length)],
    measured: Math.round((Math.random() * 3 + 0.5) * 10) / 10,
    time_period: Math.floor(Math.random() * 365) + 30,
    accuracy: Math.round(Math.random() * 0.5 * 10) / 10,
    feature: features[Math.floor(Math.random() * features.length)],
    confidence: Math.floor(Math.random() * 40) + 40
  }
}

const generateRandomChangeDetails = (): ChangeDetectionDetails => {
  const allFeatures = ['coastal', 'infrastructure (ancillary)', 'infrastructure (core)', 'vegetation', 'water'] as const
  const nonInfraFeatures = ['coastal', 'vegetation', 'water'] as const
  
  // Randomly select prior feature
  const priorFeature = allFeatures[Math.floor(Math.random() * allFeatures.length)]
  
  // If prior feature is infrastructure, current feature must be either:
  // 1. The same infrastructure type, or
  // 2. A non-infrastructure type
  // If prior feature is not infrastructure, current feature must be non-infrastructure
  const currentFeature = priorFeature.includes('infrastructure')
    ? Math.random() < 0.5  // 50% chance to keep same infrastructure type
      ? priorFeature
      : nonInfraFeatures[Math.floor(Math.random() * nonInfraFeatures.length)]
    : nonInfraFeatures[Math.floor(Math.random() * nonInfraFeatures.length)]

  return {
    priorFeature,
    currentFeature,
    pValue: Math.random() * 0.01,
    confidence: Math.floor(Math.random() * 1000) + 1
  }
}

const addDummyAlert = () => {
  const alertTypes = ['deformation-rate', 'change-detection'] as const
  const randomType = alertTypes[Math.floor(Math.random() * alertTypes.length)]
  const randomSite = sites.value[Math.floor(Math.random() * sites.value.length)]
  
  alerts.value.push({
    id: String(alerts.value.length + 1),
    type: randomType,
    site: randomSite.name,
    location: randomSite.coords as [number, number],
    likelihood: Math.random(),
    severity: Math.random(),
    startDate: new Date().toISOString().split('T')[0],
    details: randomType === 'deformation-rate' 
      ? generateRandomDeformationDetails()
      : generateRandomChangeDetails()
  })
}

const hasAlert = (siteName: string) => {
  return alerts.value.some(alert => alert.site === siteName)
}

const formatDeformationDetails = (details: DeformationDetails) => {
  return {
    threshold: `${details.threshold} cm`,
    measured: `${details.measured} cm`,
    time_period: `${details.time_period} days`,
    accuracy: `±${details.accuracy} cm`,
    feature: details.feature,
    confidence: `${details.confidence}%`
  }
}

const formatChangeDetails = (details: ChangeDetectionDetails) => {
  return {
    priorFeature: details.priorFeature,
    currentFeature: details.currentFeature,
    pValue: details.pValue.toExponential(3),
    confidence: `${details.confidence}:1`
  }
}

const openAlertDialog = (event: DataTableRowClickEvent) => {
  selectedAlert.value = event.data as Alert
  showAlertDialog.value = true
}

const dismissAlert = () => {
  if (selectedAlert.value) {
    alerts.value = alerts.value.filter(a => a.id !== selectedAlert.value!.id)
    showAlertDialog.value = false
    showActionMessage.value = `Alert ${selectedAlert.value.id} has been dismissed`
    clearActionMessageTimeout()
    showActionMessageTimeout.value = window.setTimeout(() => {
      showActionMessage.value = ''
    }, 3000)
  }
}

const flagAlert = () => {
  if (selectedAlert.value) {
    showActionMessage.value = `Alert ${selectedAlert.value.id} has been flagged`
    clearActionMessageTimeout()
    showActionMessageTimeout.value = window.setTimeout(() => {
      showActionMessage.value = ''
    }, 3000)
  }
}

const clearActionMessageTimeout = () => {
  if (showActionMessageTimeout.value) {
    clearTimeout(showActionMessageTimeout.value)
    showActionMessageTimeout.value = null
  }
}

const goToSite = () => {
  if (selectedAlert.value) {
    window.location.href = `/site/${selectedAlert.value.site}`
  }
}
</script>

<template>
  <div class="flex justify-center p-4">
    <div class="flex gap-4 max-w-7xl w-full">
      <div class="flex-grow">
        <h1 class="text-2xl font-bold mb-4">Site Status</h1>
        <div v-if="loading" class="flex justify-center items-center h-40 bg-gray-50 rounded border border-gray-200">
          <div class="text-center">
            <ProgressSpinner />
            <p class="mt-2 text-gray-500">Loading sites...</p>
          </div>
        </div>
        <DataTable v-else :value="sortedSites" class="p-datatable-sm">
          <Column field="name" header="Site Name">
            <template #body="{ data }">
              <div class="flex items-center gap-2">
                <i v-if="hasAlert(data.name)" class="pi pi-exclamation-triangle text-red-500"></i>
                <span :class="{ 'text-red-500': hasAlert(data.name) }">{{ data.name }}</span>
              </div>
            </template>
          </Column>
        </DataTable>
      </div>
      
      <div class="flex-grow-2">
        <div class="flex justify-between items-center mb-4">
          <h1 class="text-2xl font-bold">Alerts List</h1>
        </div>
        
        <div v-if="alerts.length === 0" class="w-full">
          <div class="text-center p-4 bg-gray-50 rounded border border-gray-200">
            <div class="text-gray-500">
              <i class="pi pi-info-circle text-2xl mb-2"></i>
              <p>No alerts at this time</p>
            </div>
          </div>
        </div>
        
        <DataTable 
          v-else 
          :value="alerts" 
          class="p-datatable-sm"
          @row-click="openAlertDialog"
          selectionMode="single"
          :rowClass="() => 'cursor-pointer'"
        >
          <Column field="id" header="ID" />
          <Column field="type" header="Type" />
          <Column field="site" header="Site" />
          <Column field="location" header="Location" />
          <Column field="startDate" header="Time" />
          <Column field="details" header="Details">
            <template #body="{ data }">
              <div v-if="data.type === 'deformation-rate'" class="grid grid-cols-2 gap-1 text-sm">
                <div class="font-semibold">Threshold:</div>
                <div>{{ formatDeformationDetails(data.details as DeformationDetails).threshold }}</div>
                <div class="font-semibold">Measured:</div>
                <div>{{ formatDeformationDetails(data.details as DeformationDetails).measured }}</div>
                <div class="font-semibold">Time Period:</div>
                <div>{{ formatDeformationDetails(data.details as DeformationDetails).time_period }}</div>
                <div class="font-semibold">Accuracy:</div>
                <div>{{ formatDeformationDetails(data.details as DeformationDetails).accuracy }}</div>
                <div class="font-semibold">Feature:</div>
                <div>{{ formatDeformationDetails(data.details as DeformationDetails).feature }}</div>
                <div class="font-semibold">Confidence:</div>
                <div>{{ formatDeformationDetails(data.details as DeformationDetails).confidence }}</div>
              </div>
              <div v-else class="grid grid-cols-2 gap-1 text-sm">
                <div class="font-semibold">Prior Feature:</div>
                <div>{{ formatChangeDetails(data.details as ChangeDetectionDetails).priorFeature }}</div>
                <div class="font-semibold">Current Feature:</div>
                <div>{{ formatChangeDetails(data.details as ChangeDetectionDetails).currentFeature }}</div>
                <div class="font-semibold">P Value:</div>
                <div>{{ formatChangeDetails(data.details as ChangeDetectionDetails).pValue }}</div>
                <div class="font-semibold">Confidence:</div>
                <div>{{ formatChangeDetails(data.details as ChangeDetectionDetails).confidence }}</div>
              </div>
            </template>
          </Column>
        </DataTable>

        <div class="mt-4 flex justify-end">
          <Button label="Add Dummy Alert" @click="addDummyAlert" />
        </div>
      </div>
    </div>
  </div>

  <Dialog v-model:visible="showAlertDialog" modal header="Alert Inspector" :style="{ width: '50vw' }">
    <div v-if="selectedAlert" class="p-4">
      <div class="mb-4">
        <h2 class="text-xl font-bold mb-2">Alert Details</h2>
        <div class="grid grid-cols-2 gap-2">
          <div class="font-semibold">ID:</div>
          <div>{{ selectedAlert.id }}</div>
          <div class="font-semibold">Type:</div>
          <div>{{ selectedAlert.type }}</div>
          <div class="font-semibold">Site:</div>
          <div>{{ selectedAlert.site }}</div>
          <div class="font-semibold">Date:</div>
          <div>{{ selectedAlert.startDate }}</div>
        </div>
      </div>

      <div class="mb-4">
        <h3 class="text-lg font-semibold mb-2">Measurements</h3>
        <div v-if="selectedAlert.type === 'deformation-rate'" class="grid grid-cols-2 gap-2">
          <div class="font-semibold">Threshold:</div>
          <div>{{ formatDeformationDetails(selectedAlert.details as DeformationDetails).threshold }}</div>
          <div class="font-semibold">Measured:</div>
          <div>{{ formatDeformationDetails(selectedAlert.details as DeformationDetails).measured }}</div>
          <div class="font-semibold">Time Period:</div>
          <div>{{ formatDeformationDetails(selectedAlert.details as DeformationDetails).time_period }}</div>
          <div class="font-semibold">Accuracy:</div>
          <div>{{ formatDeformationDetails(selectedAlert.details as DeformationDetails).accuracy }}</div>
          <div class="font-semibold">Feature:</div>
          <div>{{ formatDeformationDetails(selectedAlert.details as DeformationDetails).feature }}</div>
          <div class="font-semibold">Confidence:</div>
          <div>{{ formatDeformationDetails(selectedAlert.details as DeformationDetails).confidence }}</div>
        </div>
        <div v-else class="grid grid-cols-2 gap-2">
          <div class="font-semibold">Prior Feature:</div>
          <div>{{ formatChangeDetails(selectedAlert.details as ChangeDetectionDetails).priorFeature }}</div>
          <div class="font-semibold">Current Feature:</div>
          <div>{{ formatChangeDetails(selectedAlert.details as ChangeDetectionDetails).currentFeature }}</div>
          <div class="font-semibold">P Value:</div>
          <div>{{ formatChangeDetails(selectedAlert.details as ChangeDetectionDetails).pValue }}</div>
          <div class="font-semibold">Confidence:</div>
          <div>{{ formatChangeDetails(selectedAlert.details as ChangeDetectionDetails).confidence }}</div>
        </div>
      </div>

      <div class="flex justify-end gap-2 mt-4">
        <Button label="Go to Site" icon="pi pi-map-marker" @click="goToSite" />
        <Button label="Flag Alert" icon="pi pi-flag" severity="danger" @click="flagAlert" />
        <Button label="Dismiss Alert" icon="pi pi-times" severity="info" @click="dismissAlert" />
      </div>
    </div>
  </Dialog>

  <Toast position="top-right" />
  <div v-if="showActionMessage" class="fixed top-4 right-4 bg-white shadow-lg rounded-lg p-4 z-50">
    {{ showActionMessage }}
  </div>

  <Divider />
</template>

<style scoped>
.flex {
  display: flex;
}

.flex-grow {
  flex-grow: 1;
}

.flex-grow-2 {
  flex-grow: 2;
}

:deep(.p-datatable) {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  background: #f3f4f6;
  padding: 0.75rem;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
  padding: 0.75rem;
}

:deep(.p-datatable .p-datatable-tbody > tr:hover) {
  background: #f9fafb;
}

.grid {
  display: grid;
}

.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.gap-1 {
  gap: 0.25rem;
}

.gap-2 {
  gap: 0.5rem;
}

.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.font-semibold {
  font-weight: 600;
}

.cursor-pointer {
  cursor: pointer;
}

.hover\:bg-gray-50:hover {
  background-color: rgb(249 250 251);
}
</style>
